/**
 * Learner-selectable fonts for **lesson Text / Text+Audio blocks only** (bundled @fontsource where applicable).
 * Global app UI uses {@link LEARNER_UI_PERSIAN_STACK} / Inter — `--font-persian` is set to that stack in `applyLearnerPersianFont`.
 */
export type LearnerPersianFontId = 'system' | 'vazir' | 'nazanin' | 'koodak' | 'mitra';

export const LEARNER_PERSIAN_FONT_STORAGE_KEY = 'learnerPersianFontId';

/** Default lesson + UI Persian stack: bundled Vazirmatn first (same as historical app default). */
export const DEFAULT_LEARNER_PERSIAN_FONT: LearnerPersianFontId = 'vazir';

/**
 * Persian UI outside lesson blocks — bundled Vazirmatn first, then Tahoma / Arial Unicode MS fallbacks.
 */
export const LEARNER_UI_PERSIAN_STACK = '"Vazirmatn", Tahoma, "Arial Unicode MS", sans-serif';

export type LessonTypographyScope = { stack: string; multiplier: number };

/** Shown in learner settings (ids must stay in sync with server `PreferredPersianFontId`). */
export const LEARNER_PERSIAN_FONT_OPTIONS: {
  id: LearnerPersianFontId;
  labelEn: string;
  labelFa: string;
  /** Bundled @font-face family; omitted for legacy `system` id. */
  cssFamily?: string;
}[] = [
  { id: 'vazir', labelEn: 'Vazirmatn (Default)', labelFa: 'وزیرماتن', cssFamily: 'Vazirmatn' },
  { id: 'nazanin', labelEn: 'Nazanin', labelFa: 'نازنین', cssFamily: 'Amiri' },
  { id: 'koodak', labelEn: 'Koodak', labelFa: 'کودک', cssFamily: 'Lateef' },
  { id: 'mitra', labelEn: 'Mitra', labelFa: 'میترا', cssFamily: 'Noto Naskh Arabic' }
];

export function isLearnerPersianFontId(v: string | null | undefined): v is LearnerPersianFontId {
  return v === 'system' || v === 'vazir' || v === 'nazanin' || v === 'koodak' || v === 'mitra';
}

export function getLearnerPersianFontStack(id: LearnerPersianFontId): string {
  if (id === 'system' || id === 'vazir') return LEARNER_UI_PERSIAN_STACK;
  const row = LEARNER_PERSIAN_FONT_OPTIONS.find((o) => o.id === id);
  const primary = row?.cssFamily ?? 'Vazirmatn';
  return `"${primary}", ${LEARNER_UI_PERSIAN_STACK}`;
}

/** Dispatched when quick settings modal closes or lesson typography is reset from settings (listeners sync the lesson viewer). */
export const LEARNER_SETTINGS_MODAL_CLOSED_EVENT = 'app:lessonTypographyModalClosed';

/**
 * Dispatched after the learner saves a new time zone in Quick settings (region section).
 * Listeners (e.g. Profile → Progress tab) refresh data so hourly bucketing & labels match the new zone.
 */
export const LEARNER_TIMEZONE_CHANGED_EVENT = 'app:learnerTimeZoneChanged';

/** Dispatched after the learner saves a daily time target from Goals & Time. */
export const DAILY_LEARNING_GOAL_CHANGED_EVENT = 'app:learnerDailyGoalChanged';

/** Ask Goals & Time to scroll/highlight the daily commitment card. */
export const FOCUS_DAILY_COMMITMENT_EVENT = 'app:focusDailyCommitment';
export const FOCUS_DAILY_COMMITMENT_STORAGE_KEY = 'persianist:focusDailyCommitment';

export function requestDailyCommitmentFocus() {
  try {
    window.sessionStorage.setItem(FOCUS_DAILY_COMMITMENT_STORAGE_KEY, '1');
  } catch {
    /* ignore quota / private mode */
  }
  window.dispatchEvent(new CustomEvent(FOCUS_DAILY_COMMITMENT_EVENT));
}
